/**
 * Classes Service
 * 
 * Centralized service for all scheduled classes and courses operations.
 * All operations route through the classes-management edge function for:
 * - Security and validation
 * - Gym_id verification
 * - Permission checking
 * - Consistent error handling
 * 
 * Phase 5: Edge Function Refactoring
 */

import { ScheduledClass } from "@/types/types";
import { invokeWithRetry } from "@/services/edgeFunctionWrappers/common/invokeWithRetry";

interface CourseData {
  name: string;
  category_id?: string;
  default_capacity: number;
  default_duration: number;
  description?: string;
}

interface ScheduledClassData {
  name: string;
  class_date: string;
  time: string;
  instructor: string;
  category_id?: string;
  capacity: number;
  duration: string;
  course_id: string;
  calendar_id?: string | null;
}

/**
 * Nombre de tentatives SUPPLÉMENTAIRES passé à `invokeWithRetry` : la copie
 * privée faisait 3 tentatives au total, soit 2 relances.
 */
const MAX_RETRIES = 2;

class ClassesService {
  /**
   * Appel unique à `classes-management`.
   *
   * Convergence vers `edgeFunctionWrappers/common/invokeWithRetry` (audit 0.7 /
   * lot 1). L'implémentation privée qui vivait ici reproduisait le bug d'origine
   * et en ajoutait un second :
   *
   * 1. Elle testait le statut HTTP en cherchant « 401 » / « 403 » / « unauthorized »
   *    dans `error.message`. Or supabase-js renvoie un `FunctionsHttpError` dont
   *    le message vaut invariablement « Edge Function returned a non-2xx status
   *    code » — le vrai statut est sur `error.context.status`. Les branches de
   *    rafraîchissement de session n'ont donc jamais été exécutées.
   * 2. Par ricochet, `isTransient` matchait « non-2xx » pour TOUTES les erreurs
   *    HTTP : un 403 « Unauthorized: Admin role required » ou un 404 étaient
   *    rejoués trois fois avant d'échouer, la réponse ne pouvant pas changer.
   * 3. Le corps de la réponse d'erreur n'était jamais lu : le message réel du
   *    serveur était perdu, et l'UI n'affichait que « non-2xx status code ».
   *
   * La version commune corrige les trois points : statut normalisé, corps lu et
   * recopié dans `error.message`, relance uniquement sur 401 (après
   * rafraîchissement) et 503, déconnexion sur 401 persistant, détection des
   * erreurs métier renvoyées en HTTP 200.
   *
   * Ce service ne possède AUCUN repli en écriture directe : toute mutation passe
   * obligatoirement par la fonction edge. Il n'y a donc rien à arbitrer ici avec
   * `isTransportFailure` — on se contente de propager l'erreur, ce que tous les
   * appelants attendent (ils enveloppent leurs appels dans un `try/catch` qui
   * affiche un toast).
   */
  private async invokeClassesFunction<T>(
    action: string,
    body: Record<string, any>
  ): Promise<T | null> {
    const { data, error, authExpired, forbidden } = await invokeWithRetry<T>(
      'classes-management',
      body,
      MAX_RETRIES
    );

    if (authExpired) {
      // Déconnexion et redirection vers /login déjà déclenchées ; on lève pour
      // que l'appelant n'interprète pas l'absence de données comme un succès.
      console.warn(`[ClassesService.${action}] Session expired`);
      throw error ?? new Error('Session expired');
    }

    if (error) {
      if (forbidden) {
        console.warn(`[ClassesService.${action}] Forbidden:`, error.message);
      } else {
        console.error(`[ClassesService.${action}] Error:`, error);
      }
      throw error;
    }

    return data;
  }

  /**
   * Get all scheduled classes for a gym within date range
   * @param calendarId - optional filter by calendar
   */
  async getScheduledClasses(
    gymId: string,
    startDate?: string,
    endDate?: string,
    userAllowedCourses?: string[] | null,
    calendarId?: string | null
  ): Promise<ScheduledClass[]> {
    console.log('[ClassesService.getScheduledClasses]', { gymId, startDate, endDate, calendarId });

    const data = await this.invokeClassesFunction<{ classes?: ScheduledClass[] }>(
      'getScheduledClasses',
      {
        action: 'get_classes_for_schedule',
        gym_id: gymId,
        startDate,
        endDate,
        userAllowedCourses,
        calendar_id: calendarId || undefined
      }
    );

    return data?.classes || [];
  }

  /**
   * Get class availability for a specific date
   */
  async getClassAvailability(gymId: string, date: string): Promise<any[]> {
    console.log('[ClassesService.getClassAvailability]', { gymId, date });

    const data = await this.invokeClassesFunction<{ classes?: any[] }>('getClassAvailability', {
      action: 'get_class_availability',
      gym_id: gymId,
      date
    });

    return data?.classes || [];
  }

  /**
   * Add a new scheduled class
   */
  async addScheduledClass(gymId: string, classData: ScheduledClassData): Promise<boolean> {
    console.log('[ClassesService.addScheduledClass]', { gymId, classData });

    const data = await this.invokeClassesFunction<{ success?: boolean }>('addScheduledClass', {
      action: 'add_scheduled_class',
      gym_id: gymId,
      classData
    });

    return data?.success || false;
  }

  /**
   * Update a scheduled class
   */
  async updateScheduledClass(
    gymId: string,
    classId: string,
    updateData: Partial<ScheduledClassData>
  ): Promise<boolean> {
    console.log('[ClassesService.updateScheduledClass]', { gymId, classId, updateData });

    const data = await this.invokeClassesFunction<{ success?: boolean }>('updateScheduledClass', {
      action: 'update_scheduled_class',
      gym_id: gymId,
      classId,
      updateData
    });

    return data?.success || false;
  }

  /**
   * Update class capacity
   */
  async updateClassCapacity(gymId: string, classId: string, newCapacity: number): Promise<boolean> {
    console.log('[ClassesService.updateClassCapacity]', { gymId, classId, newCapacity });

    const data = await this.invokeClassesFunction<{ success?: boolean }>('updateClassCapacity', {
      action: 'update_class_capacity',
      gym_id: gymId,
      classId,
      newCapacity
    });

    return data?.success || false;
  }

  /**
   * Delete a scheduled class
   */
  async deleteClass(gymId: string, classId: string): Promise<boolean> {
    console.log('[ClassesService.deleteClass]', { gymId, classId });

    const data = await this.invokeClassesFunction<{ success?: boolean }>('deleteClass', {
      action: 'delete_class',
      gym_id: gymId,
      classId
    });

    return data?.success || false;
  }

  /**
   * Get all courses for a gym
   */
  async getCourses(gymId: string): Promise<any[]> {
    console.log('[ClassesService.getCourses]', { gymId });

    const data = await this.invokeClassesFunction<{ courses?: any[] }>('getCourses', {
      action: 'get_courses',
      gym_id: gymId
    });

    return data?.courses || [];
  }

  /**
   * Create a new course
   */
  async createCourse(gymId: string, courseData: CourseData): Promise<any> {
    console.log('[ClassesService.createCourse]', { gymId, courseData });

    const data = await this.invokeClassesFunction<{ course?: any }>('createCourse', {
      action: 'create_course',
      gym_id: gymId,
      courseData
    });

    return data?.course;
  }

  /**
   * Update a course
   */
  async updateCourse(gymId: string, courseId: string, courseData: CourseData): Promise<boolean> {
    console.log('[ClassesService.updateCourse]', { gymId, courseId, courseData });

    const data = await this.invokeClassesFunction<{ success?: boolean }>('updateCourse', {
      action: 'update_course',
      gym_id: gymId,
      courseId,
      courseData
    });

    return data?.success || false;
  }

  /**
   * Delete a course
   */
  async deleteCourse(gymId: string, courseId: string): Promise<boolean> {
    console.log('[ClassesService.deleteCourse]', { gymId, courseId });

    const data = await this.invokeClassesFunction<{ success?: boolean }>('deleteCourse', {
      action: 'delete_course',
      gym_id: gymId,
      courseId
    });

    return data?.success || false;
  }
}

// Export singleton instance
export const classesService = new ClassesService();
